<template>
  <div>
    <v-card outlined raised>
      <v-card-title>
        Mortgage Applications ({{ addTSBVZ(totalRecords) }})
        <v-spacer></v-spacer>
        <v-text-field
            v-model="searchQuery"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            clearable
            @keyup.enter="refreshData(true)"
        />
      </v-card-title>

      <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="pagination"
          :server-items-length="totalRecords"
          :footer-props="{ itemsPerPageOptions: rowsPerPageItems, showFirstLastPage: true }"
          :loading="loading"
          @update:items-per-page="refreshData"
          class="elevation-0"
      >
        <template v-slot:[`item.status`]="{ item }">
          {{ formatMortgageApplicationStatus(item.status) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  small
                  color="#00004d"
                  class="mr-1"
                  v-bind="attrs"
                  v-on="on"
                  @click="openDetails(item)"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>View Details</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                  icon
                  small
                  color="#00004d"
                  v-bind="attrs"
                  v-on="on"
                  @click="openShareDialog(item)"
              >
                <v-icon small>mdi-share-variant</v-icon>
              </v-btn>
            </template>
            <span>Share by email</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="detailsDialog" max-width="960px" scrollable>
      <v-card>
        <v-card-title>
          Mortgage Application Details
          <v-spacer></v-spacer>
          <v-btn icon @click="detailsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-simple-table>
            <thead>
            <tr>
              <th style="width: 55%">Question</th>
              <th>Answer</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(qa, index) in selectedQuestionAnswers" :key="'qa-' + index">
              <td>{{ qa.question || "-" }}</td>
              <td>{{ qa.answer || "-" }}</td>
            </tr>
            <tr v-if="selectedQuestionAnswers.length === 0">
              <td colspan="2" class="text-center">No question/answer data found.</td>
            </tr>
            </tbody>
          </v-simple-table>

          <v-divider class="my-6"></v-divider>
          <h3 class="subtitle-1 font-weight-medium mb-3">Uploaded documents</h3>
          <p v-if="selectedDocuments.length === 0" class="text--secondary mb-0">
            No documents uploaded for this application yet.
          </p>
          <v-row v-else dense>
            <v-col
                v-for="doc in selectedDocuments"
                :key="doc.field"
                cols="12"
                sm="6"
            >
              <v-card outlined class="pa-3 fill-height">
                <div class="d-flex justify-space-between align-center">
                  <div class="text-body-2 font-weight-medium pr-2">{{ doc.label }}</div>
                  <v-btn
                      small
                      color="primary"
                      outlined
                      :href="doc.url"
                      target="_blank"
                      rel="noopener"
                  >
                    <v-icon left small>mdi-open-in-new</v-icon>
                    View
                  </v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="shareDialog" max-width="520px">
      <v-card>
        <v-card-title>
          Share application by email
          <v-spacer></v-spacer>
          <v-btn icon @click="closeShareDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p v-if="shareTargetItem" class="text-body-2 text--secondary mb-4">
            Tracking:
            <strong>{{ shareTargetItem.tracking_uuid || "—" }}</strong>
            <span v-if="shareTargetItem.full_name"> · {{ shareTargetItem.full_name }}</span>
          </p>
          <p class="text-body-2 mb-2">Enter one recipient address. The email matches the view details layout (questions
            and answers, then document links).</p>
          <div class="d-flex align-start" style="gap: 8px;">
            <v-text-field
                v-model="shareEmailInput"
                label="Email address"
                type="email"
                dense
                outlined
                hide-details="auto"
                :error-messages="shareEmailFieldError"
                :disabled="shareSending"
                @keyup.enter="sendShareEmail"
            />
            <v-btn
                color="primary"
                depressed
                class="mt-1"
                :loading="shareSending"
                :disabled="!shareTargetItem || shareSending"
                @click="sendShareEmail"
            >
              Send
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeShareDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  MORTGAGE_APPLICATIONS_LIST,
  MORTGAGE_APPLICATION_SHARE,
  MORTGAGE_APPLICATION_VIEW_LOG,
} from "@/core/services/store/mortgageapplications.module";
import {buildFileUrl} from "@/utils/fileUrl";

export default {
  name: "mortgage-applications",
  data() {
    return {
      loading: false,
      pagination: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ["submitted_at"],
        sortDesc: [true],
      },
      rowsPerPageItems: [25, 50, 100, 150, 200],
      totalRecords: 0,
      searchQuery: "",
      detailsDialog: false,
      shareDialog: false,
      shareTargetItem: null,
      shareEmailInput: "",
      shareEmailFieldError: "",
      shareSending: false,
      selectedQuestionAnswers: [],
      selectedDocuments: [],
      headers: [
        {text: "Tracking UUID", value: "tracking_uuid", sortable: false},
        {text: "Name", value: "full_name", sortable: false},
        {text: "Email", value: "email", sortable: false},
        {text: "Contact", value: "contact_number", sortable: false},
        {text: "User Type", value: "user_type", sortable: false},
        {text: "Current Step", value: "current_step", sortable: false},
        {text: "Status", value: "status", sortable: false},
        {text: "Submitted At", value: "submitted_at", sortable: false},
        {text: "Actions", value: "actions", sortable: false},
      ],
      items: [],
    };
  },
  mounted() {
    this.refreshData(true);
  },
  watch: {
    shareDialog(val) {
      if (!val) {
        this.shareSending = false;
        this.shareTargetItem = null;
        this.shareEmailInput = "";
        this.shareEmailFieldError = "";
      }
    },
  },
  methods: {
    getDocumentDefinitions() {
      return [
        {field: "passport_copy", label: "Passport copy"},
        {field: "emirates_id_or_driving_licence", label: "Emirates ID / driving licence"},
        {field: "valid_visa_copy", label: "Valid visa copy"},
        {field: "bank_statement", label: "Bank statement"},
        {field: "utility_bill", label: "Utility bill"},
        {field: "resident_id", label: "Resident ID"},
        {field: "trade_licence", label: "Trade licence"},
        {field: "other_documents", label: "Other documents"},
      ];
    },
    parseDocuments(item) {
      return this.getDocumentDefinitions()
          .map((definition) => {
            const rawPath = item && item[definition.field] ? item[definition.field] : "";
            if (!rawPath) {
              return null;
            }
            return {
              field: definition.field,
              label: definition.label,
              path: rawPath,
              url: buildFileUrl(rawPath),
            };
          })
          .filter(Boolean);
    },
    loadData(logActivity = false) {
      this.loading = true;
      return new Promise((resolve) => {
        const params = this.params;
        params.length = params.itemsPerPage;
        params.start = params.page === 1 ? 0 : params.itemsPerPage * (params.page - 1);
        params["search[value]"] = params.query || "";

        let query = Object.keys(params).map((key) => {
          return encodeURIComponent(key) + "=" + encodeURIComponent(params[key]);
        }).join("&");
        if (logActivity) {
          query += "&mortgage_activity_log=1";
        }

        this.$store.dispatch(MORTGAGE_APPLICATIONS_LIST, {q: query}).then((res) => {
          const recordsRoot = res.records || {};
          const records = recordsRoot.records || recordsRoot;
          resolve({
            items: records.data || [],
            total: records.recordsFiltered || 0,
          });
          this.loading = false;
        });
      });
    },
    refreshData(logActivity = false) {
      this.loadData(logActivity).then((response) => {
        this.items = response.items;
        this.totalRecords = response.total;
      });
    },
    parseQuestionAnswers(item) {
      if (item && item.payload) {
        try {
          const parsedPayload =
              typeof item.payload === "string" ? JSON.parse(item.payload) : item.payload;
          if (Array.isArray(parsedPayload)) {
            return parsedPayload.filter((entry) => entry && (entry.question || entry.answer));
          }
        } catch (e) {
          // Continue to fallbacks.
        }
      }

      if (item && item.form_data) {
        try {
          const formData =
              typeof item.form_data === "string" ? JSON.parse(item.form_data) : item.form_data;
          if (formData && formData.question_answers) {
            const qaRaw = formData.question_answers;
            const parsedQuestionAnswers =
                typeof qaRaw === "string" ? JSON.parse(qaRaw) : qaRaw;
            if (Array.isArray(parsedQuestionAnswers)) {
              return parsedQuestionAnswers.filter((entry) => entry && (entry.question || entry.answer));
            }
          }
        } catch (e) {
          // No valid question/answer data.
        }
      }

      return [];
    },
    formatMortgageApplicationStatus(value) {
      const labels = {
        draft: "Draft",
        submitted: "Submitted",
        docs_uploaded: "Documents uploaded",
        approved: "Approved",
        rejected: "Rejected",
      };
      const key = String(value || "").toLowerCase().trim();
      if (labels[key]) {
        return labels[key];
      }
      if (!key) {
        return "—";
      }
      return key
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ");
    },
    openDetails(item) {
      this.selectedQuestionAnswers = this.parseQuestionAnswers(item);
      this.selectedDocuments = this.parseDocuments(item);
      this.detailsDialog = true;
      if (item && item.id) {
        this.$store.dispatch(MORTGAGE_APPLICATION_VIEW_LOG, {id: item.id}).catch(() => {});
      }
    },
    openShareDialog(item) {
      this.shareTargetItem = item;
      this.shareEmailInput = "";
      this.shareEmailFieldError = "";
      this.shareDialog = true;
    },
    closeShareDialog() {
      this.shareDialog = false;
    },
    isValidEmail(value) {
      const v = String(value || "").trim();
      if (!v) {
        return false;
      }
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    },
    sendShareEmail() {
      if (!this.shareTargetItem || this.shareSending) {
        return;
      }
      const raw = String(this.shareEmailInput || "").trim();
      this.shareEmailFieldError = "";
      if (!raw) {
        this.shareEmailFieldError = "Enter an email address.";
        return;
      }
      if (!this.isValidEmail(raw)) {
        this.shareEmailFieldError = "Enter a valid email address.";
        return;
      }
      this.shareSending = true;
      this.$store
          .dispatch(MORTGAGE_APPLICATION_SHARE, {
            id: this.shareTargetItem.id,
            email: raw,
          })
          .then(() => {
            this.closeShareDialog();
          })
          .catch(() => {
            /* snackbar handled in store */
          })
          .finally(() => {
            this.shareSending = false;
          });
    },
  },
  computed: {
    params() {
      return {
        ...this.pagination,
        query: this.searchQuery,
      };
    },
  },
};
</script>
